'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { getSessionStorageItem, setSessionStorageItem } from '@/lib/storage/web-storage';

import './marketing-exit-intent.css';

export type MarketingExitIntentProps = {
  sessionKey: string;
  title: string;
  body: ReactNode;
  primaryHref: string;
  primaryLabel: string;
  secondaryHref?: string;
  secondaryLabel?: string;
  dismissLabel?: string;
  /** Delay before visibilitychange can fire (avoids soft-nav false positives). */
  visibilityDelayMs?: number;
};

/**
 * Shared exit-intent overlay for marketing landings.
 * Triggers: mouse leave toward top of viewport, fast scroll-up, tab hide.
 */
export function MarketingExitIntent({
  sessionKey,
  title,
  body,
  primaryHref,
  primaryLabel,
  secondaryHref,
  secondaryLabel,
  dismissLabel = "No thanks, I'll build it later",
  visibilityDelayMs = 1500,
}: MarketingExitIntentProps) {
  const [open, setOpen] = useState(false);
  const shownRef = useRef(false);

  const hasTriggered = useCallback(
    () => shownRef.current || getSessionStorageItem(sessionKey) === '1',
    [sessionKey],
  );

  const trigger = useCallback(() => {
    if (hasTriggered()) {
      return;
    }
    shownRef.current = true;
    setSessionStorageItem(sessionKey, '1');
    setOpen(true);
  }, [hasTriggered, sessionKey]);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    const onMouseLeave = (event: MouseEvent) => {
      // clientY < 0 = cursor left through the top chrome (back button / tab bar)
      if (event.clientY < 10) {
        trigger();
      }
    };

    let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    let lastScrollTime = Date.now();

    const onScroll = () => {
      if (hasTriggered()) {
        return;
      }
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const currentTime = Date.now();
      const timeDiff = currentTime - lastScrollTime;
      const scrollDiff = lastScrollTop - currentScrollTop;
      if (scrollDiff > 60 && timeDiff < 100) {
        trigger();
      }
      lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop;
      lastScrollTime = currentTime;
    };

    let visibilityReady = false;
    const visibilityTimer = window.setTimeout(() => {
      visibilityReady = true;
    }, visibilityDelayMs);

    const onVisibilityChange = () => {
      if (visibilityReady && document.visibilityState === 'hidden') {
        trigger();
      }
    };

    // documentElement is more reliable than document for mouseleave in Chromium
    document.documentElement.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);

    const win = window as typeof window & {
      closeExitPopup?: () => void;
      triggerExitPopup?: () => void;
    };
    win.closeExitPopup = close;
    win.triggerExitPopup = trigger;

    return () => {
      window.clearTimeout(visibilityTimer);
      document.documentElement.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      delete win.closeExitPopup;
      delete win.triggerExitPopup;
    };
  }, [close, hasTriggered, trigger, visibilityDelayMs]);

  if (!open) {
    return null;
  }

  return (
    <div
      id="exit-intent-popup"
      className="kw-exit-intent-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kw-exit-intent-title"
    >
      <div className="kw-exit-intent-card">
        <button type="button" className="kw-exit-intent-close" aria-label="Close" onClick={close}>
          &times;
        </button>
        <i className="material-icons kw-exit-intent-icon">verified_user</i>
        <h2 id="kw-exit-intent-title" className="kw-exit-intent-title">
          {title}
        </h2>
        <p className="kw-exit-intent-body">{body}</p>
        <div className="kw-exit-intent-actions">
          <a href={primaryHref} className="kw-exit-intent-primary">
            {primaryLabel}
          </a>
          {secondaryHref && secondaryLabel ? (
            <a href={secondaryHref} className="kw-exit-intent-secondary">
              {secondaryLabel}
            </a>
          ) : null}
        </div>
        <p className="kw-exit-intent-dismiss-wrap">
          <button type="button" className="kw-exit-intent-dismiss" onClick={close}>
            {dismissLabel}
          </button>
        </p>
      </div>
    </div>
  );
}
